import { Agent, AgentTracingMode } from '../../types/agentTypes'
import { axiosClient } from '../axiosClient'

export interface CreateAgentData {
  name: string
  description?: string
  profile_picture_url?: string
  type: string
  ai_model_id?: string
  department_id?: string
  job_category_id?: string
  tags?: string[]
  example_questions?: string[]
  system_prompt: string
  // Enabled tool identifiers. Built-in tools use fixed literals
  // (`FILE_SEARCH`, `INTERNET_SEARCH`); per-source knowledge tools use the
  // backend-provided `retrieve_knowledge_<source>` names, so this is an open
  // string list rather than a closed enum.
  tools: string[]
  tracing_mode?: AgentTracingMode
  // Visibility (superuser-only). is_public true exposes the agent to users of
  // client_entity_ids; false (default) keeps it creator-only.
  is_public?: boolean
  client_entity_ids?: number[]
  // Flag types that do NOT apply to this agent (`flags.type` values). Empty
  // means every active flag applies — the default.
  excluded_flag_types?: string[]
  // Retrieval width ("k"): how many documents a knowledge search returns.
  // Sent as null to put the agent back on the backend default.
  knowledge_top_k?: number | null
  // Whether the main chat may route to this agent, and what the router reads
  // about it (superuser only).
  available_in_main_chat?: boolean
  routing_description?: string | null
  // Place the client context before this agent's own prompt.
  use_client_context?: boolean
}

export interface UpdateAgentData extends Partial<CreateAgentData> {
  id: string
}

export interface AgentContact {
  country: string
  name: string
  phone: string
  email: string
}

export interface AgentContactTable {
  contacts: AgentContact[]
}

export const agentFunctions = {
  fetchAgentsList: async (): Promise<Agent[]> => {
    return await axiosClient.get('api/agents/')
  },

  fetchAgent: async (agentId: string): Promise<Agent & { system_prompt?: string }> => {
    return await axiosClient.get(`api/agents/${agentId}/`)
  },

  createAgent: async (data: CreateAgentData): Promise<Agent> => {
    return await axiosClient.post('api/agents/', data)
  },

  updateAgent: async (data: UpdateAgentData): Promise<Agent> => {
    const { id, ...updateData } = data
    return await axiosClient.put(`api/agents/${id}/`, updateData)
  },

  deleteAgent: async (agentId: string): Promise<void> => {
    await axiosClient.delete(`api/agents/${agentId}/`)
  },

  updateAgentPreference: async (agentId: string, isPreferred: boolean): Promise<{ is_preferred: boolean }> => {
    return await axiosClient.put(`api/agents/${agentId}/preference/`, { is_preferred: isPreferred })
  },

  fetchAgentContacts: async (agentId: string): Promise<AgentContactTable> => {
    return await axiosClient.get(`api/agents/${agentId}/contacts/`)
  },

  createAgentContact: async (agentId: string, contact: AgentContact): Promise<AgentContact> => {
    return await axiosClient.post(`api/agents/${agentId}/contacts/`, contact)
  },

  updateAgentContact: async (agentId: string, country: string, contact: AgentContact): Promise<AgentContact> => {
    return await axiosClient.put(`api/agents/${agentId}/contacts/${country}/`, contact)
  },

  deleteAgentContact: async (agentId: string, country: string): Promise<void> => {
    await axiosClient.delete(`api/agents/${agentId}/contacts/${country}/`)
  },
}
